import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BENEFIT_KINDS,
  BENEFIT_KIND_MAP,
  type BenefitKind,
} from '@goyone/shared';
import {
  Badge,
  EmptyState,
  ErrorState,
  Modal,
  Notice,
  PageHead,
  Stat,
} from '../../components/ui';
import { Icon } from '../../components/Icon';
import { useToast } from '../../components/Toast';
import { api, qs } from '../../lib/api';
import { date, money, num, plural, WORDS } from '../../lib/format';
import { useAuth } from '../../auth/AuthProvider';

/* ==========================================================================
   الصفوف كما يرسلها الخادم
   ========================================================================== */

export interface PlanRow {
  id: string;
  name: string;
  kind: BenefitKind;
  kindLabel: string;
  kindKind: string;
  kindNote: string;
  sign: 1 | -1;
  hasShare: boolean;
  amountLabel: string;
  amount: number;
  employeeShare: number;
  currency: string;
  provider: string | null;
  notes: string | null;
  isActive: boolean;
  enrollments: number;
  liveEnrollments: number;
  monthlyCost: number;
}

export interface EnrollmentRow {
  id: string;
  plan: {
    id: string;
    name: string;
    kind: BenefitKind;
    kindLabel: string;
    kindKind: string;
    isActive: boolean;
  };
  employee: {
    id: string;
    name: string;
    code: string | null;
    department: string | null;
  };
  startsOn: string | null;
  endsOn: string | null;
  live: boolean;
  amountOverride: number | null;
  shareOverride: number | null;
  amount: number;
  sign: 1 | -1;
  employerCost: number;
  currency: string;
  notes: string | null;
  dormant: boolean;
}

interface Summary {
  plans: number;
  enrollments: number;
  covered: number;
  onDuty: number;
  monthlyCost: number;
}

interface EmployeeLite {
  id: string;
  name: string;
  code: string | null;
}

/* ==========================================================================
   الصفحة
   ========================================================================== */

/**
 * المزايا الثابتة.
 *
 * ## ولماذا بابٌ فوق بنود المرتّب
 *
 * بندُ المرتّب واقعةٌ في شهرٍ بعينه: مكافأةٌ صُرفت في مارس. والبدلُ التزامٌ
 * قائمٌ يتكرّر حتى يُوقَف — وكتابتُه كلَّ شهرٍ بيدٍ لثلاثين موظّفًا هي
 * بالضبط ما يُنسى في شهرٍ فيشتكي ثلاثون.
 *
 * ## والخطّةُ غيرُ الاشتراك فيها
 *
 * خطّةٌ واحدةٌ يشترك فيها ثلاثون بمبالغَ قد تختلف: بدلُ المواصلات يختلف
 * بالمسافة، وحصّةُ التأمين تختلف بالفئة. وتعديلُ مبلغ الخطّة يسري على من
 * لم يُكتب له مبلغٌ خاصّ.
 */
export function Benefits() {
  const { atLeast } = useAuth();
  const canEdit = atLeast('ADMIN');
  const [tab, setTab] = useState<'plans' | 'people'>('plans');

  const summary = useQuery({
    queryKey: ['benefits', 'summary'],
    queryFn: () => api<Summary>('/hr/benefits/summary'),
  });

  const s = summary.data;

  return (
    <div className="page">
      <PageHead
        title="المزايا والتأمين الطبي"
        subtitle="ما يتكرّر في المرتّب كلَّ شهر: بدلٌ يُضاف، وتأمينٌ تُخصم حصّتُه، واستقطاعٌ ثابت"
      />

      {summary.isError && (
        <ErrorState
          error={summary.error}
          onRetry={() => void summary.refetch()}
        />
      )}

      {s && (
        <div className="stats">
          <Stat label="خططٌ مفعّلة" value={num(s.plans)} note="في الكتالوج" />
          <Stat
            label="اشتراكاتٌ جارية"
            value={num(s.enrollments)}
            note={`لـ${num(s.covered)} موظّفًا`}
          />
          <Stat
            label="من على رأس العمل"
            value={num(s.onDuty)}
            note={
              s.onDuty > s.covered
                ? `و${num(s.onDuty - s.covered)} بلا ميزةٍ واحدة`
                : 'كلُّهم مشتركون'
            }
          />
          <Stat
            label="كلفةٌ شهريّة"
            value={money(s.monthlyCost)}
            note="ما تدفعه الشركة — لا ما يُخصم من الموظّفين"
          />
        </div>
      )}

      <Notice title="وأثرُها في القسيمة" icon="wallet">
        ما يُكتب هنا يدخل قسيمةَ المرتّب كلَّ شهرٍ بلا مراجعةٍ ثانية: البدلُ
        يُضاف قبل حساب التأمين والضريبة، وحصّةُ التأمين الطبّيّ تُخصم كذلك
        قبلهما — والترتيبُ يغيّر الرقم.{' '}
        <Link className="link" to="/hr/payroll">
          دورات المرتب
        </Link>
        .
      </Notice>

      <div className="tabs" role="tablist" aria-label="أقسام المزايا">
        <button
          className="tab"
          role="tab"
          aria-selected={tab === 'plans'}
          onClick={() => setTab('plans')}
        >
          الخطط
        </button>
        <button
          className="tab"
          role="tab"
          aria-selected={tab === 'people'}
          onClick={() => setTab('people')}
        >
          المشتركون
        </button>
      </div>

      {tab === 'plans' && <PlansSection canEdit={canEdit} />}
      {tab === 'people' && <PeopleSection canEdit={canEdit} />}
    </div>
  );
}

/* ==========================================================================
   الخطط
   ========================================================================== */

function PlansSection({ canEdit }: { canEdit: boolean }) {
  const [writing, setWriting] = useState(false);
  const [editing, setEditing] = useState<PlanRow | null>(null);
  const [enrolling, setEnrolling] = useState<PlanRow | null>(null);

  const list = useQuery({
    queryKey: ['benefits', 'plans'],
    queryFn: () => api<PlanRow[]>(`/hr/benefits/plans${qs({ all: '1' })}`),
  });

  const rows = list.data ?? [];

  return (
    <div className="card mt-3">
      <div className="row" style={{ gap: 'var(--sp-2)' }}>
        <h2 className="card-title" style={{ flex: 1 }}>
          خطط المزايا
        </h2>
        {canEdit && (
          <button className="btn btn-primary" onClick={() => setWriting(true)}>
            <Icon name="plus" size={15} />
            خطّة
          </button>
        )}
      </div>

      {list.isError && (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      )}
      {list.isPending && <div className="skeleton" style={{ height: 160 }} />}

      {list.data && rows.length === 0 && (
        <EmptyState
          title="لا خطط"
          text="اكتب الخطّةَ مرّةً — بدلُ مواصلاتٍ أو تأمينٌ طبّيّ — ثمّ أشرك فيها من يستحقّها، فتدخل قسائمَهم كلَّ شهرٍ بلا كتابة."
          action={
            canEdit ? (
              <button
                className="btn btn-primary"
                onClick={() => setWriting(true)}
              >
                اكتب أوّل خطّة
              </button>
            ) : undefined
          }
        />
      )}

      {rows.length > 0 && (
        <div className="table-wrap sticky-head mt-3">
          <table className="table">
            <thead>
              <tr>
                <th>الخطّة</th>
                <th>النوع</th>
                <th>المبلغ</th>
                <th>حصّة الموظّف</th>
                <th>مشتركون</th>
                <th>كلفةٌ شهريّة</th>
                {canEdit && <th />}
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr
                  key={p.id}
                  style={p.isActive ? undefined : { opacity: 0.55 }}
                >
                  <td>
                    {p.name}
                    {p.provider && (
                      <div className="tiny mute">{p.provider}</div>
                    )}
                    {!p.isActive && (
                      <div className="tiny mute">
                        موقوفة — ولا تُصرف لمن بقي مشتركًا
                      </div>
                    )}
                  </td>
                  <td>
                    <Badge kind={p.kindKind}>{p.kindLabel}</Badge>
                  </td>
                  <td className="num">{money(p.amount, p.currency)}</td>
                  <td className="num">
                    {p.hasShare ? money(p.employeeShare, p.currency) : '—'}
                  </td>
                  <td className="num">
                    {num(p.liveEnrollments)}
                    {p.enrollments > p.liveEnrollments && (
                      <div className="tiny mute">
                        و{num(p.enrollments - p.liveEnrollments)} انتهى
                      </div>
                    )}
                  </td>
                  <td className="num">
                    {p.kind === 'DEDUCTION' ? '—' : money(p.monthlyCost)}
                  </td>
                  {canEdit && (
                    <td>
                      <div className="row" style={{ gap: 4 }}>
                        <button
                          className="btn btn-sm"
                          onClick={() => setEditing(p)}
                        >
                          تعديل
                        </button>
                        {p.isActive && (
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => setEnrolling(p)}
                          >
                            أشرِك
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(writing || editing) && (
        <PlanDialog
          plan={editing}
          onClose={() => {
            setWriting(false);
            setEditing(null);
          }}
        />
      )}
      {enrolling && (
        <EnrollDialog plan={enrolling} onClose={() => setEnrolling(null)} />
      )}
    </div>
  );
}

function PlanDialog({
  plan,
  onClose,
}: {
  plan: PlanRow | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [name, setName] = useState(plan?.name ?? '');
  const [kind, setKind] = useState<BenefitKind>(plan?.kind ?? 'ALLOWANCE');
  const [amount, setAmount] = useState(String(plan?.amount ?? ''));
  const [employeeShare, setEmployeeShare] = useState(
    String(plan?.employeeShare ?? 0),
  );
  const [provider, setProvider] = useState(plan?.provider ?? '');
  const [notes, setNotes] = useState(plan?.notes ?? '');
  const [isActive, setIsActive] = useState(plan?.isActive ?? true);

  const def = BENEFIT_KIND_MAP[kind];
  const locked = plan !== null && plan.enrollments > 0;

  const save = useMutation({
    mutationFn: () =>
      api(plan ? `/hr/benefits/plans/${plan.id}` : '/hr/benefits/plans', {
        method: plan ? 'PATCH' : 'POST',
        body: {
          name,
          kind,
          amount: Number(amount) || 0,
          employeeShare: def.hasShare ? Number(employeeShare) || 0 : 0,
          provider: provider || null,
          notes: notes || null,
          isActive,
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['benefits'] });
      toast('حُفظت الخطّة.');
      onClose();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ'),
  });

  return (
    <Modal
      title={plan ? 'تعديل خطّة' : 'خطّة ميزة جديدة'}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            disabled={save.isPending || name.trim().length < 2}
            onClick={() => save.mutate()}
          >
            احفظ
          </button>
        </>
      }
    >
      <div className="form-grid">
        <div className="field span-2">
          <label className="label req" htmlFor="bp-name">
            اسم الخطّة
          </label>
          <input
            id="bp-name"
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="بدل مواصلات"
          />
          <span className="tiny mute">
            يُقرأ في قسيمة المرتّب كما يُكتب هنا.
          </span>
        </div>

        <div className="field">
          <label className="label" htmlFor="bp-kind">
            النوع
          </label>
          <select
            id="bp-kind"
            className="select"
            value={kind}
            disabled={locked}
            onChange={(e) => setKind(e.target.value as BenefitKind)}
          >
            {BENEFIT_KINDS.map((k) => (
              <option key={k} value={k}>
                {BENEFIT_KIND_MAP[k].label}
              </option>
            ))}
          </select>
          {/*
            بدلٌ صار تأمينًا يقلب الإشارة: من كان يُضاف له صار يُخصم منه،
            وقسائمُ الأشهر الماضية تبقى بالإشارة الأولى.
          */}
          <span className="tiny mute">
            {locked
              ? 'النوعُ لا يتغيّر بعد أوّل اشتراك — أوقفها واكتب غيرَها.'
              : def.description}
          </span>
        </div>

        <div className="field">
          <label className="label req" htmlFor="bp-amount">
            {def.amountLabel}
          </label>
          <input
            id="bp-amount"
            className="input num"
            type="number"
            min={0}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </div>

        {def.hasShare && (
          <div className="field">
            <label className="label" htmlFor="bp-share">
              حصّة الموظّف الشهريّة
            </label>
            <input
              id="bp-share"
              className="input num"
              type="number"
              min={0}
              value={employeeShare}
              onChange={(e) => setEmployeeShare(e.target.value)}
            />
            <span className="tiny mute">
              هي وحدَها ما يُخصم من مرتّبه — لا ما تدفعه الشركة. وصفرُها يعني
              أنّ الشركةَ تتحمّل الكلّ.
            </span>
          </div>
        )}

        <div className="field">
          <label className="label" htmlFor="bp-provider">
            الجهة المقدِّمة
          </label>
          <input
            id="bp-provider"
            className="input"
            value={provider}
            onChange={(e) => setProvider(e.target.value)}
          />
        </div>

        <div className="field span-2">
          <label className="label" htmlFor="bp-notes">
            ملاحظات
          </label>
          <textarea
            id="bp-notes"
            className="input"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>

        <div className="field span-2">
          <label className="row tiny" style={{ gap: 6 }}>
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            مفعّلة
          </label>
          {/*
            الإيقافُ هنا يوقف الصرفَ فورًا — بخلاف بقيّة الكتالوجات: ميزةٌ
            أُوقفت وبقي عليها ثلاثون اشتراكًا تعني ثلاثين قسيمةً تحمل بدلًا
            قرّرت الشركةُ إيقافَه.
          */}
          <span className="tiny mute">
            إيقافُها يوقف صرفَها فورًا لمن بقي مشتركًا — ومن أراد إبقاءَها له
            يُنهي اشتراكَه بشهرٍ يُكتب.
          </span>
        </div>
      </div>
    </Modal>
  );
}

function EnrollDialog({
  plan,
  onClose,
}: {
  plan: PlanRow;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [picked, setPicked] = useState<string[]>([]);
  const [startsOn, setStartsOn] = useState(
    `${new Date().toISOString().slice(0, 7)}-01`,
  );
  const [amount, setAmount] = useState('');
  const [share, setShare] = useState('');

  const staff = useQuery({
    queryKey: ['employees', { status: 'ACTIVE', pageSize: 200 }],
    queryFn: () =>
      api<{ items: EmployeeLite[] }>(
        '/hr/employees?status=ACTIVE&pageSize=200',
      ),
  });

  const enroll = useMutation({
    mutationFn: () =>
      api<{ added: number; skipped: number }>(
        `/hr/benefits/plans/${plan.id}/enroll`,
        {
          method: 'POST',
          body: {
            employeeIds: picked,
            startsOn,
            amount: amount === '' ? null : Number(amount),
            employeeShare: share === '' ? null : Number(share),
          },
        },
      ),
    onSuccess: (r) => {
      void queryClient.invalidateQueries({ queryKey: ['benefits'] });
      toast(
        r.skipped > 0
          ? `أُشرك ${num(r.added)} · و${num(r.skipped)} مشتركٌ من قبل`
          : `أُشرك ${num(r.added)}`,
      );
      onClose();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ'),
  });

  const toggle = (id: string) =>
    setPicked((cur) =>
      cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id],
    );

  return (
    <Modal
      title={`الاشتراك في «${plan.name}»`}
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            disabled={enroll.isPending || picked.length === 0}
            onClick={() => enroll.mutate()}
          >
            أشرِك {picked.length > 0 ? `(${num(picked.length)})` : ''}
          </button>
        </>
      }
    >
      <div className="form-grid">
        <div className="field">
          <label className="label req" htmlFor="be-start">
            يبدأ من شهر
          </label>
          <input
            id="be-start"
            className="input"
            type="date"
            value={startsOn}
            onChange={(e) => setStartsOn(e.target.value)}
          />
          {/*
            المقارنةُ بالشهر لا باليوم: من اشترك في العشرين من مارس يُصرف
            له بدلُ مارس كاملًا — البدلاتُ شهريّةٌ لا يوميّة.
          */}
          <span className="tiny mute">
            بالشهر لا باليوم: من اشترك في وسط الشهر يُصرف له الشهرُ كاملًا.
          </span>
        </div>

        <div className="field">
          <label className="label" htmlFor="be-amount">
            مبلغٌ يخصّ هؤلاء
          </label>
          <input
            id="be-amount"
            className="input num"
            type="number"
            min={0}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder={String(plan.amount)}
          />
          <span className="tiny mute">
            فارغُه يعني مبلغَ الخطّة — {money(plan.amount, plan.currency)}.
          </span>
        </div>

        {plan.hasShare && (
          <div className="field">
            <label className="label" htmlFor="be-share">
              حصّةٌ تخصّ هؤلاء
            </label>
            <input
              id="be-share"
              className="input num"
              type="number"
              min={0}
              value={share}
              onChange={(e) => setShare(e.target.value)}
              placeholder={String(plan.employeeShare)}
            />
          </div>
        )}
      </div>

      <div className="table-wrap mt-3" style={{ maxHeight: 320 }}>
        <table className="table">
          <thead>
            <tr>
              <th />
              <th>الموظّف</th>
              <th>الكود</th>
            </tr>
          </thead>
          <tbody>
            {(staff.data?.items ?? []).map((e) => (
              <tr key={e.id}>
                <td style={{ width: 40 }}>
                  <input
                    type="checkbox"
                    aria-label={`أشرِك ${e.name}`}
                    checked={picked.includes(e.id)}
                    onChange={() => toggle(e.id)}
                  />
                </td>
                <td>{e.name}</td>
                <td className="tiny mute">{e.code ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="tiny mute mt-2">
        القائمةُ أوّلُ مئتي موظّفٍ على رأس العمل — ومن كان مشتركًا يُتجاوَز بلا
        خطأ.
      </p>
    </Modal>
  );
}

/* ==========================================================================
   المشتركون
   ========================================================================== */

function PeopleSection({ canEdit }: { canEdit: boolean }) {
  const [all, setAll] = useState(false);
  const [ending, setEnding] = useState<EnrollmentRow | null>(null);

  const query = all ? { all: '1' as const } : {};

  const list = useQuery({
    queryKey: ['benefits', 'enrollments', query],
    queryFn: () => api<EnrollmentRow[]>(`/hr/benefits/enrollments${qs(query)}`),
  });

  const rows = list.data ?? [];
  const dormant = rows.filter((r) => r.dormant).length;

  return (
    <div className="card mt-3">
      <div className="row" style={{ gap: 'var(--sp-2)' }}>
        <h2 className="card-title" style={{ flex: 1 }}>
          المشتركون
        </h2>
        <button className="btn" onClick={() => setAll(!all)}>
          {all ? 'الجارية وحدَها' : 'أظهر المنتهية'}
        </button>
      </div>

      {dormant > 0 && (
        <Notice title="اشتراكاتٌ لا تُصرف" icon="alert" kind="warning">
          {plural(dormant, WORDS.record)} جاريةٌ في خططٍ أُوقفت — فلا تدخل
          القسيمة. أنهِها بشهرٍ يُكتب أو أعِد تفعيلَ خطّتها.
        </Notice>
      )}

      {list.isError && (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      )}
      {list.isPending && <div className="skeleton" style={{ height: 160 }} />}

      {list.data && rows.length === 0 && (
        <EmptyState
          title="لا مشتركين"
          text="أشرك من يستحقّ من صفحة الخطط — والاشتراكُ يدخل قسيمتَه من شهر بدئه."
        />
      )}

      {rows.length > 0 && (
        <div className="table-wrap sticky-head mt-3">
          <table className="table">
            <thead>
              <tr>
                <th>الموظّف</th>
                <th>الخطّة</th>
                <th>في القسيمة</th>
                <th>من</th>
                <th>إلى</th>
                {canEdit && <th />}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} style={r.live ? undefined : { opacity: 0.55 }}>
                  <td>
                    <Link
                      className="link"
                      to={`/hr/employees/${r.employee.id}`}
                    >
                      {r.employee.name}
                    </Link>
                  </td>
                  <td>
                    <Badge kind={r.plan.kindKind}>{r.plan.kindLabel}</Badge>{' '}
                    {r.plan.name}
                    {r.dormant && (
                      <div className="tiny" style={{ color: 'var(--warning)' }}>
                        خطّةٌ موقوفة — لا تُصرف
                      </div>
                    )}
                  </td>
                  <td className="num">
                    {r.sign === 1 ? '+' : '−'}
                    {money(r.amount, r.currency)}
                    {r.amountOverride !== null && (
                      <div className="tiny mute">مبلغٌ خاصٌّ به</div>
                    )}
                  </td>
                  <td className="tiny">{date(r.startsOn ?? '')}</td>
                  <td className="tiny">{r.endsOn ? date(r.endsOn) : 'جارٍ'}</td>
                  {canEdit && (
                    <td>
                      {r.live && (
                        <button
                          className="btn btn-sm"
                          onClick={() => setEnding(r)}
                        >
                          أنهِه
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {ending && <EndDialog row={ending} onClose={() => setEnding(null)} />}
    </div>
  );
}

function EndDialog({
  row,
  onClose,
}: {
  row: EnrollmentRow;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [endsOn, setEndsOn] = useState(
    `${new Date().toISOString().slice(0, 7)}-01`,
  );

  const end = useMutation({
    mutationFn: () =>
      api(`/hr/benefits/enrollments/${row.id}/end`, {
        method: 'POST',
        body: { endsOn },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['benefits'] });
      toast('أُنهي الاشتراك.');
      onClose();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ'),
  });

  return (
    <Modal
      title={`إنهاء اشتراك ${row.employee.name}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-danger"
            disabled={end.isPending}
            onClick={() => end.mutate()}
          >
            أنهِه
          </button>
        </>
      }
    >
      {/*
        بشهرٍ يُكتب لا بحذف: قسيمةُ الشهر الماضي صُرفت بهذا البدل، وحذفُ
        السطر يجعل من يراجعها لا يعرف من أين جاء الرقم.
      */}
      <Notice title="ما يقع" icon="info">
        يبقى الاشتراكُ في السجلّ بشهر انتهائه، فتبقى قسائمُ الأشهر الماضية
        مفهومة. وآخرُ شهرٍ يُصرف فيه هو الشهرُ المكتوبُ هنا.
      </Notice>

      <div className="field mt-3">
        <label className="label req" htmlFor="ee-end">
          آخرُ شهرٍ يُصرف فيه
        </label>
        <input
          id="ee-end"
          className="input"
          type="date"
          value={endsOn}
          onChange={(e) => setEndsOn(e.target.value)}
        />
      </div>
    </Modal>
  );
}

/* ==========================================================================
   قسمُ ملفّ الموظّف
   ========================================================================== */

interface EmployeeBenefits {
  employee: { id: string; name: string };
  rows: EnrollmentRow[];
  monthlyAdded: number;
  monthlyDeducted: number;
}

/** مزايا موظّفٍ بعينه — تُقرأ في ملفّه مع أثرها الشهريّ. */
export function EmployeeBenefitsCard({ employeeId }: { employeeId: string }) {
  const { atLeast } = useAuth();
  const canRead = atLeast('ADMIN');

  const data = useQuery({
    queryKey: ['benefits', 'employee', employeeId],
    queryFn: () =>
      api<EmployeeBenefits>(`/hr/benefits/employees/${employeeId}`),
    enabled: canRead,
  });

  if (!canRead) return null;

  const d = data.data;

  return (
    <div className="card mt-4">
      <div className="card-head">
        <h2 className="card-title">المزايا الثابتة</h2>
        <Link className="link tiny" to="/hr/benefits">
          المزايا والتأمين
        </Link>
      </div>

      {data.isPending && <div className="skeleton" style={{ height: 80 }} />}

      {d && d.rows.length === 0 && (
        <p className="tiny mute">لا مزايا ثابتةً له.</p>
      )}

      {d && d.rows.length > 0 && (
        <>
          <p className="tiny mute">
            شهريًّا: {money(d.monthlyAdded)} يُضاف، و{money(d.monthlyDeducted)}{' '}
            يُخصم.
          </p>
          <div className="table-wrap mt-2">
            <table className="table">
              <thead>
                <tr>
                  <th>الخطّة</th>
                  <th>في القسيمة</th>
                  <th>من</th>
                  <th>إلى</th>
                </tr>
              </thead>
              <tbody>
                {d.rows.map((r) => (
                  <tr key={r.id} style={r.live ? undefined : { opacity: 0.55 }}>
                    <td>
                      {r.plan.name}
                      {r.dormant && (
                        <div className="tiny mute">خطّةٌ موقوفة — لا تُصرف</div>
                      )}
                    </td>
                    <td className="num">
                      {r.sign === 1 ? '+' : '−'}
                      {money(r.amount, r.currency)}
                    </td>
                    <td className="num cell-mute">{date(r.startsOn ?? '')}</td>
                    <td className="num cell-mute">
                      {r.endsOn ? date(r.endsOn) : 'جارٍ'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
